import React, {useEffect, useState, useRef, useCallback} from 'react';

import {i18n as i18nType} from 'i18next';
import {FileRejection, useDropzone} from 'react-dropzone';
import {useTranslation} from 'react-i18next';
import {AnySchema, ValidationError} from 'yup';

import {ReactComponent as AlcoholIcon} from 'assets/svg/alcohol.svg';
import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {ReactComponent as UploadIcon} from 'assets/svg/download.svg';
import {ReactComponent as DrinkIcon} from 'assets/svg/drink.svg';
import {ReactComponent as DrugIcon} from 'assets/svg/drug.svg';
import {ReactComponent as TobaccoIcon} from 'assets/svg/tobacco.svg';
import {ReactComponent as VetIcon} from 'assets/svg/vet.svg';
import {getTemplate} from 'components/pages/Registries/mock';
import {Icon} from 'components/ui-kit/Icon';
import {IconButton} from 'components/ui-kit/IconButton';
import {colors} from 'theme';

import {ProductStatuses} from 'types/gtins';
import {readXlsxProducts} from 'utils/readXlsxProducts';

import {
    ErrorList,
    ErrorItem,
    Root,
    ModalContent,
    RootContent,
    HighlightedText,
    FileExample,
    FileExampleList,
    FileExampleItem,
    FileExampleIcon,
    FileExampleLink,
    FileExampleTitle,
    FileRejectionErrorList,
    FileRejectionErrorMessage,
    FileNameContainer,
    FileName,
    ErrorListContaienr,
} from './styled';

interface MapObject {
    [key: string]: string | boolean;
}

export interface FileFormPropsCallback {
    file?: File | null;
    data: Array<MapObject>;
    errors?: Array<string>;
    isValid?: boolean;
}
export interface FileFormProps {
    onChange(data: FileFormPropsCallback): void;
    scheme?: AnySchema;
    initialValue?: File | null;
    errors?: XLSXError[];
    serverValidate?: boolean;
}

export type XLSXError = {field: string; error: string};

enum GetDataByLanguageKeys {
    translation = 'translation',
    dictionaries = 'dictionaries',
}
const getNestedValue = (obj: any, path: string): any =>
    path.split('.').reduce((current, key) => current?.[key], obj);

const handleLanguageDiff = (key: string, item: string, i18n: i18nType) => {
    const dictionariesFields: Record<string, string> = {
        package_type: 'filePackageTypes',
        packaging_type: 'products.packageType',
        product_group_code: 'productGroups',
    };

    if (dictionariesFields[key]) {
        const dictionaries = i18n.getDataByLanguage(i18n.language)?.[
            GetDataByLanguageKeys.translation
        ]?.[GetDataByLanguageKeys.dictionaries] as Record<string, string> | undefined;

        if (!dictionaries) return item;

        const dictionary = getNestedValue(dictionaries, dictionariesFields[key]);

        const KEY_INDEX = 0;
        if (dictionary && typeof dictionary === 'object') {
            const foundItem = Object.entries(dictionary)?.find(([_key, value]) => value === item);
            return foundItem ? foundItem[KEY_INDEX] : item;
        }

        return item;
    }

    return item;
};

export const mapByTemplate = (array: Array<string[]>, file_type: string, i18n: i18nType) => {
    const file_type_translated = handleLanguageDiff('product_group_code', file_type, i18n);
    const template = getTemplate(file_type_translated);

    return array.map((arrItem) =>
        arrItem.reduce(
            (acc, item, index) => {
                Object.assign(acc, {
                    [template[index]]: handleLanguageDiff(template[index], item, i18n),
                });
                return acc;
            },
            {
                product_group_code: file_type_translated,
                is_active: true,
                status: ProductStatuses.PENDING,
            },
        ),
    );
};

export const XlsxMultipleFilesForm = ({
    onChange,
    scheme,
    initialValue,
    errors: errorsList,
    serverValidate,
}: FileFormProps) => {
    const {t, i18n} = useTranslation();
    const didMountRef = useRef(false);
    const [file, fileChanged] = useState<File | null>(initialValue || null);
    const [errors, errorsChanged] = useState<Array<string> | null>(null);
    const [data, dataChanged] = useState<Array<MapObject>>([]);
    const [fileRejectionMessages, setFileRejectionMessages] = useState<Array<string>>([]);
    const DRAGNDROP_LOCATION = 'pages.documents.invoices.create.invoiceLines.xlsxModal';

    const handleChangeFile = (droppedFile: File | null): void => {
        if (!droppedFile) {
            fileChanged(null);
            dataChanged([]);
            return;
        }

        const {file} = readXlsxProducts(droppedFile, (data: any, _: File, file_type: string) => {
            const mappedData = mapByTemplate(data, file_type, i18n);
            dataChanged(mappedData);
        });

        if (serverValidate) return fileChanged(file);

        if (file) {
            fileChanged(file);
        } else {
            fileChanged(null);
            dataChanged([]);
        }
    };

    useEffect(() => {
        if (!serverValidate && scheme && file && didMountRef.current) {
            scheme
                .validate(data, {abortEarly: false})
                .then(() => {
                    errorsChanged([]);
                })
                .catch((err) => {
                    const errors: Array<string> = [];
                    err.inner.forEach((e: ValidationError) => {
                        const path = e.path ? e.path.match(/\[(.*)\]/) ?? [] : [];
                        const stringCount = path.pop();
                        errors.push(
                            stringCount
                                ? `${t('components.xlsxForm.row')}: ${
                                      stringCount ? parseInt(stringCount, 10) + 2 : '-'
                                  }: ` + (i18n.exists(e.message) ? t(e.message) : e.message)
                                : e.message,
                        );
                    });
                    errorsChanged(errors);
                });
        } else if (didMountRef.current && !file) {
            errorsChanged(null);
        }
    }, [data]);

    useEffect(() => {
        if (!file && didMountRef.current) {
            dataChanged([]);
        }
    }, [file]);

    useEffect(() => {
        if (didMountRef.current) {
            onChange({
                file,
                data,
                errors: errors === null ? [] : errors,
                isValid: errors !== null && !errors.length,
            });
        } else {
            didMountRef.current = true;
        }
    }, [file, data, errors]);

    const onDrop = useCallback(
        (fl: File[], fileRejections: FileRejection[]) => {
            setFileRejectionMessages([]);
            if (fileRejections.length > 0) {
                setFileRejectionMessages(() =>
                    fileRejections[0].errors.map((error) => error.message),
                );
            }
            if (fl.length > 0) {
                const selectedFile = fl[0];
                handleChangeFile(selectedFile);
            }
        },
        [file, handleChangeFile],
    );
    const {getRootProps, getInputProps, isDragActive} = useDropzone({onDrop, accept: '.xlsx,.xls'});

    const fileExamples = [
        {
            title: 'tobacco',
            link: '/examples/Products_data_template_TOBACCO.xlsx',
            icon: TobaccoIcon,
        },
        {
            title: 'drinks',
            link: '/examples/Products_data_template_DRINKS.xlsx',
            icon: DrinkIcon,
        },
        {
            title: 'alcohol',
            link: '/examples/Products_data_template_ALCOHOL.xlsx',
            icon: AlcoholIcon,
        },
        {
            title: 'medications',
            link: '/examples/Products_data_template_MEDICATIONS.xlsx',
            icon: DrugIcon,
        },
        {
            title: 'veterinary',
            link: '/examples/Products_data_template_VETERINARY.xlsx',
            icon: VetIcon,
        },
    ];

    return (
        <>
            <ModalContent>
                <FileExample>
                    <FileExampleTitle>{t('components.xlsxForm.needHelp')}</FileExampleTitle>
                    <FileExampleList>
                        {fileExamples.map((example) => (
                            <FileExampleItem key={example.title}>
                                <FileExampleIcon>
                                    <Icon
                                        source={example.icon}
                                        fillColor={colors.brandPrimary}
                                        width={20}
                                        height={20}
                                    />
                                </FileExampleIcon>
                                <FileExampleLink href={example.link} rel="noreferrer">
                                    {t('components.xlsxForm.downloadTemplate')}
                                </FileExampleLink>
                            </FileExampleItem>
                        ))}
                    </FileExampleList>
                </FileExample>
                {file ? (
                    <FileNameContainer>
                        <FileName>{file.name}</FileName>
                        <IconButton
                            icon={
                                <Icon
                                    source={CrossIcon}
                                    width={11}
                                    height={11}
                                    fillColor={colors.textPrimary}
                                />
                            }
                            onClick={() => handleChangeFile(null)}
                            type="button"
                        />
                    </FileNameContainer>
                ) : (
                    <Root isDrag={isDragActive} {...getRootProps()}>
                        <input {...getInputProps()} />
                        {isDragActive ? (
                            <p>{t('pages.dashboard.dragHere')}</p>
                        ) : (
                            <RootContent>
                                <Icon source={UploadIcon} width={16} height={16} />
                                <p>
                                    <HighlightedText>
                                        {t(`${DRAGNDROP_LOCATION}.dragAndDrop`)}
                                    </HighlightedText>{' '}
                                    {t(`${DRAGNDROP_LOCATION}.aFileHere`)}{' '}
                                    <HighlightedText>
                                        {t(`${DRAGNDROP_LOCATION}.chooseFile`)}
                                    </HighlightedText>
                                </p>
                            </RootContent>
                        )}
                    </Root>
                )}
                {fileRejectionMessages.length > 0 && (
                    <FileRejectionErrorList>
                        {fileRejectionMessages.map((message, i) => (
                            <FileRejectionErrorMessage key={i}>{message}</FileRejectionErrorMessage>
                        ))}
                    </FileRejectionErrorList>
                )}
            </ModalContent>
            {serverValidate && errorsList && errorsList.length > 0 && (
                <ErrorListContaienr>
                    <ErrorList>
                        {errorsList?.map(({field, error}, i) => (
                            <ErrorItem key={i}>
                                Field <b>{field}:</b> {error}
                            </ErrorItem>
                        ))}
                    </ErrorList>
                </ErrorListContaienr>
            )}
        </>
    );
};
